import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExtractionLogDetail } from "../components/ExtractionLogDetail";
import { PageTitle } from "../components/PageTitle";
import { Button } from "../components/ui";
import { useToast } from "../components/Toast";
import { apiGet } from "../lib/api";

interface LogBrief {
  run_id: string;
  status?: string;
  run_type?: string;
  target_company?: string;
  saved_at?: string;
  error_count?: number;
  evidence_count?: number;
  urls_discovered?: number;
  projects_synthesized?: number;
  main_contractor?: string;
}

export function LogsPage() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [logs, setLogs] = useState<LogBrief[]>([]);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const selected = params.get("run") || "";

  const loadList = () =>
    apiGet<{ logs: LogBrief[] }>("/api/logs").then((d) => setLogs(d.logs || []));

  const loadDetail = (runId: string) => {
    if (!runId) {
      setDetail(null);
      return;
    }
    apiGet<{ log: Record<string, unknown> }>(`/api/logs/${encodeURIComponent(runId)}`)
      .then((d) => setDetail(d.log))
      .catch((e: Error) => toast(e.message, true));
  };

  useEffect(() => {
    loadList().catch((e: Error) => toast(e.message, true));
  }, [toast]);

  useEffect(() => {
    loadDetail(selected);
    if (!selected) return;
    const iv = window.setInterval(() => {
      loadDetail(selected);
      loadList();
    }, 4000);
    return () => window.clearInterval(iv);
  }, [selected]);

  const isPhase2 = detail?.run_type === "phase2_extraction";
  const isRunning = detail?.status === "running";

  return (
    <>
      <header className="h-14 px-6 border-b border-outline-variant bg-surface-container flex items-center justify-between shrink-0">
        <span className="font-data-label text-data-label uppercase text-on-surface-variant">
          Execution traces
        </span>
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            loadList();
            loadDetail(selected);
          }}
        >
          Refresh
        </Button>
      </header>
      <main className="flex-1 flex flex-col overflow-hidden min-h-0">
        <PageTitle phaseNum={0} phaseLabel="Ops" title="Execution logs" />
        <div className="flex flex-1 min-h-0">
          <div className="w-80 border-r border-outline-variant overflow-y-auto custom-scrollbar p-3">
            {!logs.length && (
              <p className="text-body-sm text-on-surface-variant p-2">
                No logs yet — run Phase 1, Phase 2 extraction, or Phase 3 synthesis.
              </p>
            )}
            {logs.map((l) => (
              <button
                key={l.run_id}
                type="button"
                onClick={() => {
                  setParams({ run: l.run_id });
                }}
                className={`w-full text-left p-2 rounded mb-1 border-l-2 ${
                  l.run_id === selected
                    ? "border-primary bg-surface-container-high"
                    : "border-transparent hover:bg-surface-variant"
                }`}
              >
                <div className="font-data-mono text-data-mono text-primary">{l.run_id}</div>
                <div className="text-body-sm mt-1">{l.target_company || "—"}</div>
                <div className="text-data-label text-on-surface-variant mt-1">
                  {l.status}
                  {l.run_type === "phase2_extraction" ? " · Phase 2 extraction" : ""}
                  {l.run_type === "family_classification" ? " · Project families" : ""}
                  {l.run_type === "phase1" || !l.run_type ? " · Phase 1" : ""}
                </div>
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
            {!selected && (
              <p className="text-body-sm text-on-surface-variant">Select a run from the list</p>
            )}
            {detail && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-data-mono text-primary">{String(detail.run_id)}</h3>
                  {isRunning ? (
                    <span className="text-body-sm text-primary animate-pulse">running…</span>
                  ) : null}
                </div>
                <p className="text-body-sm">
                  Status: {String(detail.status)} · Type: {String(detail.run_type)}
                </p>
                {detail.phase2_url ? (
                  <Link
                    to={String(detail.phase2_url)}
                    className="text-primary text-sm hover:underline"
                  >
                    Open evidence desk
                  </Link>
                ) : null}

                {isPhase2 ? (
                  <ExtractionLogDetail log={detail} />
                ) : (
                  <pre className="text-xs font-data-mono p-3 bg-surface-container-lowest panel-border max-h-[60vh] overflow-auto">
                    {JSON.stringify(detail, null, 2)}
                  </pre>
                )}

                <details className="mt-6">
                  <summary className="text-xs text-on-surface-variant cursor-pointer hover:text-primary">
                    Raw JSON
                  </summary>
                  <pre className="text-xs font-data-mono p-3 bg-surface-container-lowest panel-border max-h-[40vh] overflow-auto mt-2">
                    {JSON.stringify(detail, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
